
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Plan, PricingModalProps } from "./pricing/types";
import { usePlans } from "@/hooks/usePlans";
import { useProductName } from "@/hooks/useProductName";
import { useUserSubscriptions } from "@/hooks/useUserSubscriptions";
import { useTaxRate } from "@/hooks/useTaxRate";
import { handlePaidSubscription } from "./pricing/subscriptionHandler";
import { ProductDetails } from "./pricing/ProductDetails";
import { FeaturesSection } from "./pricing/FeaturesSection";
import { PlansSection } from "./pricing/PlansSection";
import { useSubscriptions } from "@/hooks/useSubscriptions";

export function PricingModal({ isOpen, onClose, selectedPath, productId }: PricingModalProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [subscribing, setSubscribing] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { plans, loading } = usePlans(productId);
  const productName = useProductName(productId);
  const userSubscriptions = useUserSubscriptions(isOpen, productId);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { taxRate } = useTaxRate(isOpen);
  const [productFeatures, setProductFeatures] = useState<string | null>(null);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorSchoolId, setErrorSchoolId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { forceRefresh } = useSubscriptions();

  useEffect(() => {
    const checkAuthStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
      
      if (session?.user?.email) {
        setUserEmail(session.user.email);
      }
    };
    
    if (isOpen) {
      checkAuthStatus();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchProductFeatures = async () => {
      if (!productId) return;
      
      try {
        const { data, error } = await supabase
          .from('products')
          .select('features')
          .eq('id', productId)
          .single();
        
        if (error) throw error;
        if (data) {
          setProductFeatures(data.features || null);
        }
      } catch (error) {
        console.error('Error fetching product features:', error);
      }
    };
    
    if (isOpen && productId) {
      fetchProductFeatures();
    }
  }, [isOpen, productId]);

  const handleSubscribe = async (plan: Plan) => {
    if (!isLoggedIn) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to subscribe to this plan.",
        variant: "destructive"
      });
      onClose();
      navigate("/auth");
      return;
    }

    setSubscribing(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error("No authenticated user found");

      await handlePaidSubscription({
        user,
        plan,
        productId,
        productName,
        taxRate
      });
      
      toast({
        title: "Subscription Started",
        description: `You've successfully subscribed to the ${plan.plan_name} plan.`,
      });
      
      forceRefresh();
      
      onClose();
      navigate("/subscriptions");
    } catch (error: any) {
      console.error('Error handling subscription:', error);
      
      // Check for school registration error
      if (error.message && error.message.includes('SCHOOL_NOT_REGISTERED:')) {
        // Extract school ID from error message
        const schoolId = error.message.split(':')[1];
        console.log('School not registered:', schoolId);
        setErrorSchoolId(schoolId);
        setErrorMessage("Sekolah dengan Kode IDN ini belum terdaftar di Kasflow. Silakan hubungi administrator untuk mendaftarkan sekolah Anda.");
        setShowErrorDialog(true);
      } else if (error.message && error.message.includes('Failed to create subscription')) {
        // Handle general subscription creation error
        const isSchoolNotRegistered = error.message.includes('School not registered') || 
                                    error.message.includes('school not registered');
        
        if (isSchoolNotRegistered) {
          // Extract school ID if available in the error message
          const schoolIdMatch = error.message.match(/(\d+)/);
          const schoolId = schoolIdMatch ? schoolIdMatch[0] : "unknown";
          setErrorSchoolId(schoolId);
          setErrorMessage("Sekolah dengan Kode IDN ini belum terdaftar di Kasflow. Silakan hubungi administrator untuk mendaftarkan sekolah Anda.");
          setShowErrorDialog(true);
        } else {
          toast({
            variant: "destructive",
            title: "Error",
            description: error.message || "Failed to create subscription. Please try again.",
          });
        }
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to create subscription. Please try again.",
        });
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
    setErrorSchoolId(null);
    setErrorMessage("");
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl p-0 overflow-hidden">
          <div className="flex flex-col md:flex-row h-full">
            <ProductDetails productId={productId} isOpen={isOpen} />
            <PlansSection 
              plans={plans}
              loading={loading}
              isLoggedIn={isLoggedIn}
              subscribing={subscribing}
              userSubscriptions={userSubscriptions}
              productId={productId}
              onSubscribe={handleSubscribe}
            />
            <FeaturesSection features={productFeatures} />
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sekolah Tidak Terdaftar</AlertDialogTitle>
            <AlertDialogDescription>
              {errorMessage || `Sekolah dengan Kode IDN ${errorSchoolId} belum terdaftar di Kasflow. Silakan hubungi administrator untuk mendaftarkan sekolah Anda.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleErrorDialogClose}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
